import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const schemaData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/contact#webpage`,
          url: `${SITE_URL}/contact`,
          name: 'Contact Faasley Foods | Get in Touch for Products & Partnerships',
          description: 'Contact Faasley Foods for product inquiries, wholesale orders, investment opportunities, or general questions. We respond within 24 hours.',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'Contact', item: `${SITE_URL}/contact` },
            ],
          },
        },
        {
          '@type': 'ContactPage',
          name: 'Contact Faasley Foods',
          url: `${SITE_URL}/contact`,
          description: 'Reach out to Faasley Foods for product inquiries, wholesale orders, or investment opportunities.',
          mainEntity: {
            '@type': 'Organization',
            name: 'Faasley Foods',
            legalName: 'Faasleyfoods Pvt. Ltd.',
            telephone: '+91-8800889959',
            email: 'pari211284@gmail.com',
            url: SITE_URL,
            address: {
              '@type': 'PostalAddress',
              addressCountry: 'IN',
              addressLocality: 'India',
            },
            openingHoursSpecification: [
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                opens: '09:00',
                closes: '18:00',
              },
            ],
          },
        },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-contact';
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);
    document.title = 'Contact Faasley Foods | Get in Touch for Products & Partnerships';
    return () => {
      const el = document.getElementById('schema-contact');
      if (el) el.remove();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      if (formData.phone) body.append('phone', formData.phone);
      body.append('subject', formData.subject);
      body.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d77ao0iqo7glkto3e5ag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const contactInfo = [
    { icon: 'ri-map-pin-line', label: 'Address', value: 'Faasleyfoods Pvt. Ltd., India' },
    { icon: 'ri-phone-line', label: 'Phone', value: '+91 8800889959' },
    { icon: 'ri-mail-line', label: 'Email', value: 'pari211284@gmail.com' },
    { icon: 'ri-global-line', label: 'Website', value: 'www.faasley.com' },
    { icon: 'ri-time-line', label: 'Business Hours', value: 'Mon-Sat: 9AM - 6PM IST' },
  ];

  return (
    <div className="min-h-screen bg-cream dark:bg-gray-900 transition-colors duration-300">
      <Navbar />

      {/* Page Hero */}
      <div className="pt-[240px] md:pt-[260px]">
        <div className="h-[280px] md:h-[350px] relative overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/7299ea90894f27c75d7afaf07bf1429b.jpg"
            alt="Contact Faasley"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brown/80 to-terracotta/60" />
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
              <span className="text-saffron font-medium text-sm uppercase tracking-widest mb-3 block">
                We're Here to Help
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                Contact Us
              </h1>
              <p className="text-white/85 text-lg max-w-xl">
                Reach out for product inquiries, wholesale orders, or investment opportunities. We respond within 24 hours.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Content */}
      <section className="py-16 md:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="grid lg:grid-cols-3 gap-8">

            {/* Left: Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-2xl p-6">
                <h2 className="font-display font-bold text-brown text-xl mb-6">
                  Get in Touch
                </h2>
                <div className="space-y-5">
                  {contactInfo.map((info, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-saffron/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className={`${info.icon} text-saffron`}></i>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs uppercase tracking-wider">{info.label}</p>
                        <p className="text-brown font-medium text-sm">{info.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Social */}
              <div className="bg-forest rounded-2xl p-6 text-white">
                <h3 className="font-bold text-lg mb-2">Follow Us</h3>
                <p className="text-white/60 text-sm mb-5">Stay updated with our latest products and offers</p>
                <div className="flex items-center gap-3">
                  {[
                    { icon: 'ri-instagram-line', label: 'Instagram', href: '#' },
                    { icon: 'ri-facebook-circle-line', label: 'Facebook', href: '#' },
                    { icon: 'ri-whatsapp-line', label: 'WhatsApp', href: 'https://wa.me/918800889959' },
                    { icon: 'ri-linkedin-box-line', label: 'LinkedIn', href: '#' },
                  ].map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="nofollow noreferrer"
                      aria-label={social.label}
                      className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-saffron transition-colors cursor-pointer"
                    >
                      <i className={`${social.icon} text-lg`}></i>
                    </a>
                  ))}
                </div>
              </div>

              {/* Quick Response Promise */}
              <div className="bg-saffron/10 border border-saffron/20 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-saffron/20 rounded-lg">
                    <i className="ri-flashlight-line text-saffron text-lg"></i>
                  </div>
                  <h3 className="font-bold text-brown">Quick Response</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We typically respond to all enquiries within <strong>24 hours</strong> on business days. For urgent matters, please call us directly.
                </p>
              </div>
            </div>

            {/* Right: Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-6 md:p-8">
                <div className="mb-6">
                  <h2 className="font-display font-bold text-brown text-2xl mb-1">
                    Send us a Message
                  </h2>
                  <p className="text-gray-500 text-sm">Fill in the form below and we'll get back to you shortly.</p>
                </div>

                {submitted ? (
                  <div className="bg-green-50 border border-green-200 rounded-2xl p-10 text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                      <i className="ri-check-double-line text-green-600 text-3xl"></i>
                    </div>
                    <h3 className="text-green-800 font-display font-bold text-2xl mb-2">Message Sent!</h3>
                    <p className="text-green-600 mb-6">
                      Thank you for reaching out to Faasley. We've received your message and will get back to you within 24 hours.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="bg-saffron text-white font-semibold px-8 py-3 rounded-xl hover:bg-saffron-dark transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-saffron focus:ring-2 focus:ring-saffron/20 transition-all bg-gray-50 focus:bg-white"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-saffron focus:ring-2 focus:ring-saffron/20 transition-all bg-gray-50 focus:bg-white"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number <span className="text-gray-400 font-normal">(Optional)</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-saffron focus:ring-2 focus:ring-saffron/20 transition-all bg-gray-50 focus:bg-white"
                          placeholder="+91 XXXXX XXXXX"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Subject <span className="text-red-400">*</span>
                        </label>
                        <select
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-saffron focus:ring-2 focus:ring-saffron/20 transition-all bg-gray-50 focus:bg-white cursor-pointer"
                        >
                          <option value="">Select a subject</option>
                          <option value="General Inquiry">General Inquiry</option>
                          <option value="Product Query">Product Query</option>
                          <option value="Investment Opportunity">Investment Opportunity</option>
                          <option value="Partnership">Partnership</option>
                          <option value="Wholesale / Bulk Order">Wholesale / Bulk Order</option>
                          <option value="Complaint / Feedback">Complaint / Feedback</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message <span className="text-red-400">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-saffron focus:ring-2 focus:ring-saffron/20 transition-all resize-none bg-gray-50 focus:bg-white"
                        placeholder="Write your message here... (max 500 characters)"
                      />
                      <div className="flex justify-between items-center mt-1">
                        <span className={`text-xs ${formData.message.length > 480 ? 'text-red-400' : 'text-gray-400'}`}>
                          {formData.message.length > 500 ? 'Message too long' : ''}
                        </span>
                        <span className={`text-xs ${formData.message.length > 480 ? 'text-red-400' : 'text-gray-400'}`}>
                          {formData.message.length}/500
                        </span>
                      </div>
                    </div>

                    {submitError && (
                      <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
                        <i className="ri-error-warning-line text-red-500 text-sm"></i>
                        <p className="text-red-600 text-xs">{submitError}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting || formData.message.length > 500}
                      className="w-full bg-saffron hover:bg-saffron-dark text-white font-semibold py-4 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          Sending Message...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-fill"></i>
                          Send Message
                        </>
                      )}
                    </button>

                    <p className="text-gray-400 text-xs text-center">
                      We respect your privacy. Your information will never be shared with third parties.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
